// src/components/LinkInBioProduct.jsx
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRocket,
  faCalculator,
  faLink,
  faShieldHalved,
  faClockRotateLeft,
  faHandshake,
  faTag,
  faCircleCheck,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import './LinkInBioProduct.css';

export default function LinkInBioProduct() {
  const [screen, setScreen] = useState('tools'); // 'links' | 'tools'
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 992px)');
    const update = () => setIsDesktop(mq.matches);
    update();
    if (mq.addEventListener) mq.addEventListener('change', update);
    else mq.addListener(update);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', update);
      else mq.removeListener(update);
    };
  }, []);

  // Als dit component op de homepage staat, voeg ?from=home toe
  const isHostHome = typeof window !== 'undefined' && window.location.pathname === '/';

  const perks = [
    'Meteen indicatieve prijzen — minder heen-en-weer',
    'Rekentools die leads kwalificeren',
    'Aanvraag met alle context in mailbox/CRM',
    'Volledig in jouw huisstijl en tone-of-voice',
    'Link-in-bio hub voor Instagram / TikTok / LinkedIn',
    'Conversiegericht, snel en mobiel-first',
  ];

  const steps = [
    { title: 'Intake (30 min)', desc: 'Doelen, services en prijslogica vastleggen.' },
    { title: 'Design & tool', desc: 'Huisstijl, copy en rekentool-formules op maat.' },
    { title: 'Live & meten', desc: 'Koppeling e-mail/WhatsApp/CRM, optimalisatie.' },
  ];

  const srcBase =
    screen === 'tools'
      ? '/links/tools?embed=1'
      : '/links?embed=1';

  const src = isHostHome ? `${srcBase}&from=home` : srcBase;

  return (
    <section className="libp-section">
      <div className="container">

        {/* PROMO / DISCOUNT CARD */}
        <div className="promo-card shadow-sm" aria-label="Actie: Link-in-bio los af te nemen">
          <div className="promo-left">
            <span className="promo-badge">
              <FontAwesomeIcon icon={faTag} className="me-2" />
              Actie
            </span>
            <h3 className="promo-title">Link-in-bio pagina <span className="text-orange">los af te nemen</span></h3>
            <p className="promo-sub">Inclusief basis-SEO, snelle laadtijd en e-mail/WhatsApp-koppeling.</p>
          </div>
          <div className="promo-right">
            <div className="promo-price">
              <span className="promo-currency">€</span>
              <span className="promo-amount">19,99</span>
              <span className="promo-period">/ p.m.</span>
            </div>
            <div className="promo-fine">excl. btw · maandabonnement</div>
            <Link to="/contact" className="btn btn-orange btn-lg promo-cta">Start nu</Link>
          </div>
        </div>

        {/* HEADER */}
        <header className="text-center">
          <h2 className="libp-title">
            Link-in-bio <span className="text-orange">met rekentools</span>
          </h2>
          <p className="libp-subtitle">
            Eén slimme pagina die vragen opvangt, prijzen inschat en leads kwalificeert — jij focust op uitvoeren.
          </p>
        </header>

        <div className="row align-items-center gy-5 gx-lg-5 libp-row">
          {/* PHONE FIRST */}
          <div className="col-12 col-lg-6 order-1 order-lg-2">
            <div className="libp-phone-wrap">
              <div className="libp-phone-toolbar" role="tablist" aria-label="Preview keus">
                <button
                  type="button"
                  role="tab"
                  aria-selected={screen === 'tools'}
                  className={`libp-chip ${screen === 'tools' ? 'active' : ''}`}
                  onClick={() => setScreen('tools')}
                >
                  <FontAwesomeIcon icon={faCalculator} className="me-2" />
                  Rekentool
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={screen === 'links'}
                  className={`libp-chip ${screen === 'links' ? 'active' : ''}`}
                  onClick={() => setScreen('links')}
                >
                  <FontAwesomeIcon icon={faLink} className="me-2" />
                  Links-hub
                </button>
              </div>

              {/* iPhone mockup */}
              <div className="libp-iphone">
                <div className="libp-edge" />
                <div className="libp-glass" />
                <div className="libp-screen" aria-label="Live preview">
                  <iframe
                    title="Link-in-bio preview"
                    src={src}
                    loading="lazy"
                    className={`libp-iframe ${isDesktop ? 'is-desktop' : 'no-interact'}`}
                    referrerPolicy="no-referrer"
                    scrolling={isDesktop ? 'yes' : 'no'}
                    tabIndex={isDesktop ? 0 : -1}
                    aria-hidden={isDesktop ? 'false' : 'true'}
                  />
                </div>
                <div className="libp-notch" />
                <span className="libp-btn-side libp-btn-top" />
                <span className="libp-btn-side libp-btn-mid" />
                <span className="libp-btn-power" />
              </div>

              {/* 'Bekijk live' met chevron */}
              <div className="libp-live">
                <a
                  href={screen === 'tools' ? '/links/tools' : '/links'}
                  className="libp-live-link"
                >
                  Bekijk live <FontAwesomeIcon icon={faChevronRight} className="ms-1" />
                </a>
              </div>
            </div>
          </div>

          {/* PITCH */}
          <div className="col-12 col-lg-6 order-2 order-lg-1">
            <article className="libp-card">
              <h3 className="libp-heading">Waarom dit werkt</h3>
              <p className="libp-intro">
                Bezoekers krijgen direct een <strong>bandbreedte-prijs</strong>, jij ontvangt een
                <strong> complete aanvraag</strong> met context. Resultaat: <strong>snellere offertes</strong>,
                <strong> betere leads</strong> en <strong>minder tijdverlies</strong>.
              </p>

              <ul className="libp-perk-list list-unstyled" aria-label="Belangrijkste voordelen">
                {perks.map((text, i) => (
                  <li key={i} className="libp-perk">
                    <FontAwesomeIcon icon={faCircleCheck} className="libp-perk-icon" />
                    <span className="libp-perk-text">{text}</span>
                  </li>
                ))}
              </ul>

              <div className="libp-stats" aria-label="Resultaten">
                <div className="libp-stat">
                  <div className="libp-stat-val">−40%</div>
                  <div className="libp-stat-key">Bel/mail heen-en-weer</div>
                </div>
                <div className="libp-stat">
                  <div className="libp-stat-val">+25–30%</div>
                  <div className="libp-stat-key">Meer aanvragen</div>
                </div>
                <div className="libp-stat">
                  <div className="libp-stat-val">~2 min</div>
                  <div className="libp-stat-key">Invultijd klant</div>
                </div>
              </div>

              <blockquote className="libp-quote">
                “Aanvragen zijn <strong>veel gerichter</strong> en offertes gaan sneller de deur uit.”
                <cite>— Bouwbedrijf, Den Haag</cite>
              </blockquote>

              <div className="libp-steps">
                {steps.map((s, i) => (
                  <div key={i} className="libp-step">
                    <div className="libp-step-dot" />
                    <div>
                      <div className="libp-step-title">{s.title}</div>
                      <div className="libp-step-desc">{s.desc}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Trust-cards */}
              <div className="libp-trust-grid mt-3">
                <div className="trust-card">
                  <div className="trust-icon">
                    <FontAwesomeIcon icon={faShieldHalved} />
                  </div>
                  <div className="trust-text">
                    <div className="trust-title">Huisstijl & copy op maat</div>
                    <div className="trust-sub">Consistente merkbeleving</div>
                  </div>
                </div>

                <div className="trust-card">
                  <div className="trust-icon">
                    <FontAwesomeIcon icon={faClockRotateLeft} />
                  </div>
                  <div className="trust-text">
                    <div className="trust-title">Snel live</div>
                    <div className="trust-sub">Doorlopend verbeteren</div>
                  </div>
                </div>

                <div className="trust-card">
                  <div className="trust-icon">
                    <FontAwesomeIcon icon={faHandshake} />
                  </div>
                  <div className="trust-text">
                    <div className="trust-title">Koppelingen</div>
                    <div className="trust-sub">E-mail • WhatsApp • CRM</div>
                  </div>
                </div>
              </div>

              {/* Conversie & social + add-on/los */}
              <p className="libp-meta mt-3">
                De <strong>links-hub</strong> is conversiegericht en gemaakt voor social kanalen
                (Instagram, TikTok, LinkedIn) om bezoekers razendsnel te sturen naar de juiste actie —
                bijvoorbeeld direct naar een <strong>rekentool</strong>. Die rekentool leveren we
                als <strong>add-on</strong> of als <strong>los onderdeel</strong>.
              </p>

              {/* Hoofd-CTA’s */}
              <div className="d-grid gap-3 mt-3">
                <Link to="/contact" className="btn btn-orange btn-lg w-100">
                  <FontAwesomeIcon icon={faRocket} className="me-2" />
                  Ontvang gratis demo
                </Link>
                <a
                  href="https://wa.me/31644109856"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-whatsapp btn-lg w-100"
                >
                  <FontAwesomeIcon icon={faWhatsapp} className="me-2" />
                  Snelle vraag via WhatsApp
                </a>
              </div>

              <div className="libp-note mt-3">
                Naadloos naast je <strong>website-lease</strong> pakketten. Geen hoge opstartkosten.
              </div>
            </article>
          </div>
        </div>
      </div>
    </section>
  );
}
